import { SizeTier, SIZE_TIERS, BREAKPOINTS, CHART_WIDTH_MAP, DEFAULT_CHART_HEIGHTS } from '../../lib/api/templates';

export interface GridItem {
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    type: string;
    sizeTier: SizeTier;
    order: number;
    aspectRatio: string;
    _editBp?: string;
}

/**
 * Determines which breakpoint applies to the current screen width
 */
export const detectBreakpoint = (width: number): string => {
    // Iterating through breakpoints in order (xs → sm → md → lg → xl)
    const sortedKeys = ['xs', 'sm', 'md', 'lg', 'xl'];
    for (const key of sortedKeys) {
        if (width <= BREAKPOINTS[key].maxWidth) {
            return key;
        }
    }
    return 'xl'; // Fallback
};


/**
 * Calculates how many columns a chart should occupy for a given breakpoint
 */
export const calculateChartWidth = (sizeTier: SizeTier, breakpoint: string): number => {
    const tierMap = CHART_WIDTH_MAP[sizeTier];
    if (tierMap && tierMap[breakpoint] !== undefined) {
        return tierMap[breakpoint];
    }
    // Fallback: use lg breakpoint value, or preferred from SIZE_TIERS
    if (tierMap && tierMap['lg'] !== undefined) {
        return tierMap['lg'];
    }
    return SIZE_TIERS[sizeTier]?.preferred || 12;
};

/**
 * Calculates how many rows a chart should occupy for a given breakpoint.
 * Uses the Visibility Consistency Formula: H = Href * (W / Wref)^k
 */
export const calculateChartHeight = (sizeTier: SizeTier, breakpoint: string): number => {
    const refHeight = DEFAULT_CHART_HEIGHTS[sizeTier] ?? 4;
    const bp = BREAKPOINTS[breakpoint] || BREAKPOINTS.lg;

    const tierRefWidth = calculateChartWidth(sizeTier, 'xl');
    const currentWidth = calculateChartWidth(sizeTier, breakpoint);
    const widthRatio = currentWidth / tierRefWidth;

    const k = Math.max(0.3, Math.min(1.0, 1.25 - (refHeight / 10)));
    const dynamicHeight = Math.round(refHeight * Math.pow(widthRatio, k));

    const minH = (sizeTier === 'xs' || refHeight <= 2) ? 1 : 3;
    return Math.max(minH, Math.min(dynamicHeight, bp.maxHeightUnits));
};

/**
 * Determines the correct w/h for a chart at a breakpoint.
 */
export const resolveChartDimensions = (chart: GridItem, bpKey: string): { w: number; h: number } => {
    const bp = BREAKPOINTS[bpKey] || BREAKPOINTS.lg;

    let finalW: number;
    const tierW = calculateChartWidth(chart.sizeTier, bpKey);

    if (chart._editBp === bpKey && (chart.w || 0) > 0) {
        finalW = chart.w;
    } else if ((chart.w || 0) > 0 && (chart.w || 0) <= 2) {
        finalW = Math.min(chart.w, bp.columns);
    } else {
        finalW = tierW;
    }

    if (chart._editBp === bpKey && (chart.h || 0) > 0) {
        return { w: finalW, h: chart.h };
    }

    return { w: finalW, h: calculateChartHeight(chart.sizeTier, bpKey) };
};
/**
 * Detects line groups with sublines from a layout.
 * 
 * A "line" is a set of charts whose tops are within a tolerance of each other.
 * A chart becomes "subline 2" of the previous line if:
 *   - It has a horizontal neighbor (overlapping or adjacent) from the previous line
 *   - Its top is at least 1 unit below the top of that neighbor (chart.y >= neighbor.y + 1)
 *   - Its top is at most 2 units below the bottom of the lowest chart in the previous line
 * 
 * Line groups are recounted from actual positions every time this is called.
 * Returns an array of line groups, each containing subline1 and subline2 arrays,
 * sorted left-to-right within each subline.
 */
interface PlacedChart {
    chart: GridItem;
    x: number;
    y: number;
    w: number;
    h: number;
}

interface LineGroup {
    subline1: PlacedChart[];
    subline2: PlacedChart[];
}

const detectLineGroups = (placedCharts: PlacedChart[]): LineGroup[] => {
    if (placedCharts.length === 0) return [];

    // Sort by Y first, then X
    const sorted = [...placedCharts].sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y;
        return a.x - b.x;
    });

    const lineGroups: LineGroup[] = [];
    const assigned = new Set<string>();

    // First pass: identify line groups (charts on similar Y positions)
    const yTolerance = 1; // Charts within 1 grid unit of each other's Y are same line

    for (const chart of sorted) {
        if (assigned.has(chart.chart.i)) continue;

        // Start a new line group
        const group: LineGroup = { subline1: [], subline2: [] };
        group.subline1.push(chart);
        assigned.add(chart.chart.i);

        // Find other charts on the same line (similar Y)
        for (const other of sorted) {
            if (assigned.has(other.chart.i)) continue;
            if (Math.abs(other.y - chart.y) <= yTolerance) {
                group.subline1.push(other);
                assigned.add(other.chart.i);
            }
        }

        lineGroups.push(group);
    }

    // Second pass: check if any line group should merge as subline 2 of the previous group.
    // Uses horizontal neighbor proximity instead of vertical center.
    const mergedGroups: LineGroup[] = [];

    for (let i = 0; i < lineGroups.length; i++) {
        const currentGroup = lineGroups[i];

        if (mergedGroups.length === 0) {
            mergedGroups.push(currentGroup);
            continue;
        }

        const prevGroup = mergedGroups[mergedGroups.length - 1];

        // Can't add another subline 2 if one already exists
        if (prevGroup.subline2.length > 0) {
            mergedGroups.push(currentGroup);
            continue;
        }

        let shouldMerge = false;
        for (const currentChart of currentGroup.subline1) {
            for (const prevChart of prevGroup.subline1) {
                // Check if charts are horizontal neighbors (overlapping or touching)
                const currentRight = currentChart.x + currentChart.w;
                const prevRight = prevChart.x + prevChart.w;
                const hasHorizontalNeighbor =
                    // Overlapping horizontally
                    (currentChart.x < prevRight && currentRight > prevChart.x) ||
                    // Adjacent (touching sides)
                    (currentRight === prevChart.x) ||
                    (prevRight === currentChart.x);

                if (hasHorizontalNeighbor) {
                    // Condition 1: top of current chart is at least 1 unit below neighbor's top
                    const atLeastOneBelow = currentChart.y >= prevChart.y + 1;

                    // Condition 2: current chart's top is WITHIN the neighbor's vertical span.
                    const neighborBottom = prevChart.y + prevChart.h;
                    const startsWithinNeighbor = currentChart.y < neighborBottom;

                    if (atLeastOneBelow && startsWithinNeighbor) {
                        shouldMerge = true;
                        break;
                    }
                }
            }
            if (shouldMerge) break;
        }

        if (shouldMerge) {
            // Merge as subline 2
            prevGroup.subline2 = currentGroup.subline1;
        } else {
            mergedGroups.push(currentGroup);
        }
    }

    // Sort each subline left-to-right by X
    for (const group of mergedGroups) {
        group.subline1.sort((a, b) => a.x - b.x);
        group.subline2.sort((a, b) => a.x - b.x);
    }

    return mergedGroups;
};

/**
 * Produces the interleaved order from line groups for responsive reflowing.
 * 
 * For a line with subline1=[A,B,C,D] and subline2=[E,F,G,H]:
 *   Order: A, E, B, F, C, G, D, H
 * 
 * For a line with subline1=[A,B,C,D] and subline2=[null,F,null,null]:
 *   We align subline2 items by their column position relative to subline1.
 *   Order: A, B, F, C, D  (F goes after B because it's under B's column area)
 */
const interleaveLineGroup = (group: LineGroup): PlacedChart[] => {
    const result: PlacedChart[] = [];
    const { subline1, subline2 } = group;

    if (subline2.length === 0) {
        // No subline 2, just return subline 1 in order
        return [...subline1];
    }

    // Map subline2 charts to their closest subline1 chart by X overlap
    // Each subline2 chart is paired with the subline1 chart it's "under"
    const sub2Assignments = new Map<number, PlacedChart[]>(); // subline1 index -> subline2 charts under it

    for (const s2Chart of subline2) {
        let bestIdx = -1;
        let bestOverlap = -1;

        for (let i = 0; i < subline1.length; i++) {
            const s1Chart = subline1[i];
            // Calculate horizontal overlap
            const overlapStart = Math.max(s1Chart.x, s2Chart.x);
            const overlapEnd = Math.min(s1Chart.x + s1Chart.w, s2Chart.x + s2Chart.w);
            const overlap = Math.max(0, overlapEnd - overlapStart);

            if (overlap > bestOverlap) {
                bestOverlap = overlap;
                bestIdx = i;
            }
        }

        if (bestIdx === -1) {
            // Fallback: assign to the closest subline1 chart by X
            bestIdx = 0;
            let minDist = Infinity;
            for (let i = 0; i < subline1.length; i++) {
                const dist = Math.abs(subline1[i].x - s2Chart.x);
                if (dist < minDist) {
                    minDist = dist;
                    bestIdx = i;
                }
            }
        }

        if (!sub2Assignments.has(bestIdx)) {
            sub2Assignments.set(bestIdx, []);
        }
        sub2Assignments.get(bestIdx)!.push(s2Chart);
    }

    // Interleave: for each subline1 chart, output it, then its subline2 charts
    for (let i = 0; i < subline1.length; i++) {
        result.push(subline1[i]);
        const assigned = sub2Assignments.get(i);
        if (assigned) {
            // Sort subline2 charts under this slot by X
            assigned.sort((a, b) => a.x - b.x);
            result.push(...assigned);
        }
    }

    return result;
};

/**
 * Standard skyline placement for a list of charts at a given breakpoint.
 */
const skylinePlacement = (charts: GridItem[], bpKey: string): GridItem[] => {
    const bp = BREAKPOINTS[bpKey];
    const availableColumns = bp.columns;
    const colHeights = new Array(availableColumns).fill(0);

    return charts.map(chart => {
        const { w: chartWidth, h: chartHeight } = resolveChartDimensions(chart, bpKey);

        let bestX = 0;
        let bestY = Infinity;
        for (let x = 0; x <= availableColumns - chartWidth; x++) {
            let maxY = 0;
            for (let i = x; i < x + chartWidth; i++) {
                maxY = Math.max(maxY, colHeights[i]);
            }
            if (maxY < bestY) {
                bestY = maxY;
                bestX = x;
            }
        }

        for (let i = bestX; i < bestX + chartWidth; i++) {
            colHeights[i] = bestY + chartHeight;
        }

        return { ...chart, x: bestX, y: bestY, w: chartWidth, h: chartHeight };
    });
};

/**
 * Generates responsive layouts for all breakpoints using the subline-aware flow engine.
 */
export const generateResponsiveLayouts = (items: GridItem[]) => {
    const layouts: Record<string, GridItem[]> = {};

    if (items.length === 0) {
        Object.keys(BREAKPOINTS).forEach(bpKey => {
            layouts[bpKey] = [];
        });
        return layouts;
    }

    // 1. Sort all charts by their 'order' field (ascending)
    const sortedCharts = [...items].sort((a, b) => {
        if (a.order !== b.order) {
            return a.order - b.order;
        }
        return a.i.localeCompare(b.i);
    });

    // Determine if items have been manually positioned on a specific breakpoint
    const editBp = sortedCharts[0]?._editBp || null;
    const isLargeBp = (bp: string) => bp === 'xl' || bp === 'lg';

    // 2. Generate the reference layout for xl (used for subline detection)
    //    If editBp is a large bp, use stored positions as reference.
    //    Otherwise, compute via skyline.
    let refPlaced: PlacedChart[];

    if (editBp && isLargeBp(editBp)) {
        // Positions are from a large screen — use stored x/y as reference.
        // Recalculate w/h from size maps unless the chart was custom-resized.
        const xlItems = sortedCharts.map(c => {
            const { w, h } = resolveChartDimensions(c, 'xl');
            return { ...c, w, h };
        });
        refPlaced = xlItems.map(c => ({
            chart: c, x: c.x, y: c.y, w: c.w, h: c.h
        }));

        layouts['xl'] = xlItems;
        layouts['lg'] = sortedCharts.map(c => {
            const { w, h } = resolveChartDimensions(c, 'lg');
            return { ...c, w, h };
        });
    } else {
        // Compute xl/lg via skyline
        const xlResult = skylinePlacement(sortedCharts, 'xl');
        layouts['xl'] = xlResult;
        refPlaced = xlResult.map(c => ({
            chart: c, x: c.x, y: c.y, w: c.w, h: c.h
        }));
        layouts['lg'] = skylinePlacement(sortedCharts, 'lg');
    }

    // 3. Detect line groups and sublines from the reference layout
    const lineGroups = detectLineGroups(refPlaced);

    // 4. Build the interleaved order from line groups
    const interleavedOrder: PlacedChart[] = [];
    for (const group of lineGroups) {
        interleavedOrder.push(...interleaveLineGroup(group));
    }

    // 5. Generate layouts for small breakpoints
    const smallBps = ['md', 'sm', 'xs'];
    for (const bpKey of smallBps) {
        if (editBp === bpKey) {
            // Pass through stored positions for the edited breakpoint
            layouts[bpKey] = sortedCharts.map(c => ({ ...c }));
        } else {
            // Compute via interleaved skyline
            const bp = BREAKPOINTS[bpKey];
            const availableColumns = bp.columns;
            const colHeights = new Array(availableColumns).fill(0);

            layouts[bpKey] = interleavedOrder.map(placed => {
                const chart = placed.chart;
                const { w: chartWidth, h: chartHeight } = resolveChartDimensions(chart, bpKey);

                let bestX = 0;
                let bestY = Infinity;
                for (let x = 0; x <= availableColumns - chartWidth; x++) {
                    let maxY = 0;
                    for (let i = x; i < x + chartWidth; i++) {
                        maxY = Math.max(maxY, colHeights[i]);
                    }
                    if (maxY < bestY) {
                        bestY = maxY;
                        bestX = x;
                    }
                }

                for (let i = bestX; i < bestX + chartWidth; i++) {
                    colHeights[i] = bestY + chartHeight;
                }

                return {
                    ...chart,
                    x: bestX,
                    y: bestY,
                    w: chartWidth,
                    h: chartHeight
                };
            });
        }
    }

    return layouts;
};

/**
 * Performs order-based re-assignment for items
 * Ensures order values are clean integers after drag/drop
 */
export const reorderGridItems = (items: GridItem[]): GridItem[] => {
    return [...items]
        .sort((a, b) => a.order - b.order)
        .map((item, index) => ({
            ...item,
            order: index
        }));
};

/**
 * Checks if two items overlap (AABB collision)
 */
const checkCollision = (a: { x: number, y: number, w: number, h: number }, b: { x: number, y: number, w: number, h: number }): boolean => {
    if (a.x + a.w <= b.x) return false;
    if (a.x >= b.x + b.w) return false;
    if (a.y + a.h <= b.y) return false;
    if (a.y >= b.y + b.h) return false;
    return true;
};

/**
 * Applies horizontal compaction ("Gravity Left") to the items.
 * A chart moves left until it hits x=0 or another chart.
 * This enforces the rule: "Left of each chart should always be to the right of another chart or aligned with left of grid."
 * 
 * @param items The items to compact
 * @param activeItemId Optional. If provided, ONLY this item will be snapped left. Others remain touched.
 */
export const compactHorizontal = (items: GridItem[], activeItemId?: string): GridItem[] => {
    // Clone to avoid mutating input directly, and round to integers for safe logic
    const compacted = items.map(item => ({
        ...item,
        x: Math.round(item.x),
        y: Math.round(item.y),
        w: Math.round(item.w),
        h: Math.round(item.h)
    }));

    const itemsToProcess = activeItemId
        ? compacted.filter(i => i.i === activeItemId)
        : compacted;

    // Sort items to process by x asc
    itemsToProcess.sort((a, b) => a.x - b.x);

    for (const item of itemsToProcess) {
        let currentX = item.x;

        // Try moving left step by step
        while (currentX > 0) {
            const proposedX = currentX - 1;
            const proposedItem = { ...item, x: proposedX };

            // Check for collisions with all other items
            const hasCollision = compacted.some(other => {
                if (other.i === item.i) return false;
                return checkCollision(proposedItem, other);
            });

            if (!hasCollision) {
                currentX = proposedX;
            } else {
                // Hit something, stop here
                break;
            }
        }
        item.x = currentX;
    }

    return compacted;
};
